/**
 * Enhanced Cache Manager for Kissarity
 * Comprehensive cache control and busting solution
 */

interface CacheConfig {
  version: string;
  timestamp: number;
  sessionId: string;
  forceRefresh: boolean;
}

class EnhancedCacheManager {
  private config: CacheConfig;
  private cacheVersion: string;

  constructor() {
    this.cacheVersion = process.env.REACT_APP_VERSION || '1.0.0';
    this.config = {
      version: this.cacheVersion,
      timestamp: Date.now(),
      sessionId: this.generateSessionId(),
      forceRefresh: false
    };
    
    this.initializeCache();
    if (typeof window !== 'undefined') {
      this.enableNetworkRefresh();
    }
  }

  private generateSessionId(): string {
    const existing = sessionStorage.getItem('kissaria-session-id');
    if (existing) return existing;
    
    const sessionId = Math.random().toString(36).substring(2, 15) + Date.now().toString(36);
    sessionStorage.setItem('kissaria-session-id', sessionId);
    return sessionId;
  }

  private initializeCache(): void {
    const currentVersion =
      localStorage.getItem('kissariti-cache-version') ??
      localStorage.getItem('kissaria-cache-version');
    if (currentVersion !== this.cacheVersion) {
      this.clearAllCaches();
      localStorage.setItem('kissariti-cache-version', this.cacheVersion);
      localStorage.removeItem('kissaria-cache-version');
    }

    // Listen for cache update events
    window.addEventListener('storage', this.handleStorageChange.bind(this));
    
    // Auto-refresh cache every 30 minutes in development
    if (process.env.NODE_ENV === 'development') {
      setInterval(() => this.refreshCache(), 30 * 60 * 1000);
    }
  }

  private handleStorageChange(event: StorageEvent): void {
    if (event.key === 'kissariti-cache-version' && event.newValue !== this.cacheVersion) {
      this.forceRefresh();
    }
  }

  // Enhanced cache busting with multiple parameters
  public bustCache(url: string, type: 'image' | 'script' | 'style' | 'api' = 'image'): string {
    if (!url) return url;

    // Skip if already has cache busting parameters
    if (url.includes('?') && (url.includes('v=') || url.includes('cb='))) {
      return url;
    }

    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const separator = url.includes('?') ? '&' : '?';

    switch (type) {
      case 'image':
        return `${url}${separator}v=${this.cacheVersion}&t=${timestamp}&r=${random}&cb=1&s=${this.config.sessionId}`;
      case 'script':
        return `${url}${separator}v=${this.cacheVersion}&t=${timestamp}&cb=2`;
      case 'style':
        return `${url}${separator}v=${this.cacheVersion}&t=${timestamp}&cb=3`;
      case 'api':
        return `${url}${separator}v=${this.cacheVersion}&t=${timestamp}&cb=4`;
      default:
        return `${url}${separator}v=${this.cacheVersion}&t=${timestamp}&cb=1`;
    }
  }

  // Special handling for slider images
  public bustSliderCache(imagePath: string): string {
    if (!imagePath) return imagePath;
    
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const separator = imagePath.includes('?') ? '&' : '?';
    
    return `${imagePath}${separator}slider=1&t=${timestamp}&r=${random}&s=${this.config.sessionId}&cb=slider&v=${this.cacheVersion}`;
  }

  // Force refresh all images on the page
  public refreshAllImages(): void {
    const images = document.querySelectorAll('img');
    images.forEach((img, index) => {
      const imgElement = img as HTMLImageElement;
      const currentSrc = imgElement.src;
      
      if (currentSrc) {
        const baseSrc = currentSrc.split('?')[0];
        let newSrc: string;
        
        // Special handling for slider images
        if (currentSrc.includes('/slider/')) {
          newSrc = this.bustSliderCache(baseSrc);
        } else {
          newSrc = this.bustCache(baseSrc, 'image');
        }
        
        // Force reload by temporarily changing src
        imgElement.src = '';
        setTimeout(() => {
          imgElement.src = newSrc;
        }, 10 + index * 5); // Stagger reloads to prevent overwhelming
      }
    });
  }

  // Refresh specific image types
  public refreshImagesByType(type: 'slider' | 'product' | 'banner' | 'all'): void {
    let selector: string;
    
    switch (type) {
      case 'slider':
        selector = 'img[src*="/slider/"]';
        break;
      case 'product':
        selector = 'img[src*="/images/"]';
        break;
      case 'banner':
        selector = 'img[src*="/Banners/"]';
        break;
      case 'all':
        this.refreshAllImages();
        return;
      default:
        return;
    }

    const images = document.querySelectorAll(selector);
    images.forEach((img, index) => {
      const imgElement = img as HTMLImageElement;
      const currentSrc = imgElement.src;
      
      if (currentSrc) {
        const baseSrc = currentSrc.split('?')[0];
        const newSrc = type === 'slider' ? this.bustSliderCache(baseSrc) : this.bustCache(baseSrc, 'image');
        
        imgElement.src = '';
        setTimeout(() => {
          imgElement.src = newSrc;
        }, 10 + index * 5);
      }
    });
  }

  // Clear all browser caches
  public async clearAllCaches(): Promise<void> {
    try {
      console.log('🔄 Clearing all caches...');

      // Clear browser cache
      if ('caches' in window) {
        const cacheNames = await caches.keys();
        await Promise.all(
          cacheNames.map(cacheName => {
            console.log(`🗑️ Deleting cache: ${cacheName}`);
            return caches.delete(cacheName);
          })
        );
        console.log('✅ Browser caches cleared');
      }

      // Clear service worker cache
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'CLEAR_CACHE',
          timestamp: Date.now()
        });
        console.log('✅ Service worker cache cleared');
      }

      // Clear localStorage cache-related items
      const keysToRemove = [
        'kissariti-cache-version',
        'kissaria-cache-version',
        'app-version',
        'image-cache',
        'api-cache'
      ];
      
      keysToRemove.forEach(key => {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      });
      console.log('✅ LocalStorage cache cleared');

      // Update cache version
      this.config.timestamp = Date.now();
      this.config.forceRefresh = true;
      
      console.log('✅ All caches cleared successfully');
    } catch (error) {
      console.error('❌ Error clearing caches:', error);
    }
  }

  // Force refresh the entire application
  public async forceRefresh(): Promise<void> {
    console.log('🔄 Force refreshing application...');
    await this.clearAllCaches();
    
    // Add a small delay to ensure caches are cleared
    setTimeout(() => {
      window.location.reload();
    }, 100);
  }

  // Refresh cache without full reload
  public refreshCache(): void {
    console.log('🔄 Refreshing cache...');
    this.config.timestamp = Date.now();
    this.refreshAllImages();
    
    // Refresh CSS and JS files
    const links = document.querySelectorAll('link[rel="stylesheet"]');
    links.forEach((link, index) => {
      const linkElement = link as HTMLLinkElement;
      const currentHref = linkElement.href;
      
      if (currentHref) {
        const baseHref = currentHref.split('?')[0];
        const newHref = this.bustCache(baseHref, 'style');
        
        setTimeout(() => {
          linkElement.href = newHref;
        }, 50 + index * 10);
      }
    });
  }

  // Check for cache issues
  public checkCacheHealth(): Promise<boolean> {
    return new Promise((resolve) => {
      const testImage = new Image();
      const testUrl = this.bustCache('/logo.png', 'image');
      
      testImage.onload = () => {
        console.log('✅ Cache health check passed');
        resolve(true);
      };
      
      testImage.onerror = () => {
        console.warn('⚠️ Cache health check failed');
        resolve(false);
      };
      
      testImage.src = testUrl;
    });
  }

  // Get cache statistics
  public async getCacheStats(): Promise<any> {
    const stats = {
      version: this.cacheVersion,
      timestamp: this.config.timestamp,
      sessionId: this.config.sessionId,
      browserCaches: 0,
      localStorageItems: 0,
      serviceWorkerActive: false
    };

    // Count browser caches
    if ('caches' in window) {
      const cacheNames = await caches.keys();
      stats.browserCaches = cacheNames.length;
    }

    // Count localStorage items
    stats.localStorageItems = localStorage.length;

    // Check service worker
    if ('serviceWorker' in navigator) {
      stats.serviceWorkerActive = !!navigator.serviceWorker.controller;
    }

    return stats;
  }

  // Auto-refresh on network reconnect
  public enableNetworkRefresh(): void {
    window.addEventListener('online', () => {
      console.log('🌐 Network reconnected, refreshing cache...');
      this.refreshCache();
    });
  }

  // Handle navigation errors
  public handleNavigationError(): void {
    console.log('🚨 Navigation error detected, attempting cache fix...');
    this.forceRefresh();
  }
}

// Create singleton instance
const cacheManager = new EnhancedCacheManager();

// Export functions for easy use
export const bustCache = (url: string, type?: 'image' | 'script' | 'style' | 'api') => 
  cacheManager.bustCache(url, type);

export const bustSliderCache = (imagePath: string) => 
  cacheManager.bustSliderCache(imagePath);

export const refreshAllImages = () => 
  cacheManager.refreshAllImages();

export const refreshImagesByType = (type: 'slider' | 'product' | 'banner' | 'all') => 
  cacheManager.refreshImagesByType(type);

export const clearAllCaches = () => 
  cacheManager.clearAllCaches();

export const forceRefresh = () => 
  cacheManager.forceRefresh();

export const refreshCache = () => 
  cacheManager.refreshCache();

export const checkCacheHealth = () => 
  cacheManager.checkCacheHealth();

export const getCacheStats = () => 
  cacheManager.getCacheStats();

export const enableNetworkRefresh = () => 
  cacheManager.enableNetworkRefresh();

export const handleNavigationError = () => 
  cacheManager.handleNavigationError();

// Export the manager instance for advanced usage
export default cacheManager; 